import React from 'react'
import {useTranslation} from 'react-i18next'
import {SvgProps} from 'react-native-svg'

import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import {NamedColors} from 'src/designSystem/colors'
import Box from 'src/designSystem/components/atoms/Box/Box'
import WarningIconSvg from 'src/assets/icons/WarningOutlinedFilled.svg'
import CycleIconSvg from 'src/assets/icons/CycleFilled.svg'
import PFSvg from 'src/designSystem/components/atoms/PFSvg/PFSvg'

export enum DateBubblePaymentDateStatus {
  FAILED = 'failed', // red bubble
  WARNING = 'warning', // yellow bubble
  NEXT = 'next', // dark blue bubble
  UPCOMING = 'upcoming', // light blue bubble
  IN_PROGRESS = 'in_progress', // green checkmark
}

/**
 * A single payment date for use with a DateBubble.
 */
export type DateBubblePaymentDate =
  | {
      status: DateBubblePaymentDateStatus.IN_PROGRESS
      key: string
      date?: Date
    }
  | {
      date: Date
      status?: DateBubblePaymentDateStatus // defaults to UPCOMING
      amount: number
      /**
       * A unique key for this payment date.
       */
      key: string
    }

export type DateBubbleProps = {
  paymentDate: DateBubblePaymentDate
  isFailed: boolean
  isAutoRepresented?: boolean // marking this as options for now until we have this date from the BE
  testID: string
}

const monthNamesMap: Record<number, string> = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
}

enum BadgeType {
  Failed = 'failed',
  AutoRepresented = 'autoRepresented',
}

type BadgeConfig = {
  testIdSuffix: string
  iconSvg: React.FC<SvgProps>
  iconSize: {width: number; height: number}
}

type BadgeProps = {
  type: BadgeType
  testID: string
}

const badgeConfigs: Record<BadgeType, BadgeConfig> = {
  [BadgeType.Failed]: {
    testIdSuffix: 'Failed',
    iconSvg: WarningIconSvg,
    iconSize: {width: 18, height: 16},
  },
  [BadgeType.AutoRepresented]: {
    testIdSuffix: 'AutoRepresented',
    iconSvg: CycleIconSvg,
    iconSize: {width: 19, height: 19},
  },
}

/**
 * A badge component that displays an icon
 * @param props - Component props
 * @param props.type - The type of badge to display
 * @param props.testID - The test ID for the badge
 */
export const Badge: React.FC<BadgeProps> = ({type, testID}) => {
  const config = badgeConfigs[type]

  return (
    <Box
      boxStyle={{position: 'absolute', top: 0, right: 0}}
      testID={`${testID}-${config.testIdSuffix}`}
    >
      <PFSvg testID={testID} SvgUri={config.iconSvg} svgProps={config.iconSize} />
    </Box>
  )
}

/**
 * Renders a circle containing a month and day of that month in different states
 * defined by DateBubbleStates.
 *
 * @param props - Component props
 * @param props.paymentDate - The date object to display
 * @param props.isFailed - Whether the payment has failed
 * @param props.isAutoRepresented - Whether the payment is auto-represented
 * @param props.testID - Test ID for component testing
 */
export const DateBubble: React.FC<DateBubbleProps> = ({
  paymentDate,
  testID,
  isFailed,
  isAutoRepresented,
}) => {
  const {t} = useTranslation('DateBubble')

  const month = (paymentDate?.date?.getMonth() ?? -1) + 1
  const day = paymentDate?.date?.getDate()
  const textColor = isFailed ? NamedColors.SILVER : NamedColors.BLACK

  return (
    <Box
      border={{
        width: 0.75,
        color: NamedColors.WHITESMOKE,
      }}
      radius={33}
      height={66}
      width={66}
      align="center"
      justify="center"
      boxStyle={{
        backgroundColor: NamedColors.WHITE,
        shadowColor: NamedColors.SILVER,
        shadowOffset: {width: 0, height: 4},
        shadowOpacity: 0.16,
        shadowRadius: 6,
        elevation: 3,
      }}
      testID={testID}
    >
      {isFailed ? <Badge type={BadgeType.Failed} testID={testID} /> : null}
      {isAutoRepresented ? <Badge type={BadgeType.AutoRepresented} testID={testID} /> : null}
      <PFText variant="p_lg" color={textColor} testID={`${testID}-Day`}>
        {day}
      </PFText>
      <PFText variant="p_sm" color={textColor} testID={`${testID}-Month`}>
        {t(monthNamesMap[month])}
      </PFText>
    </Box>
  )
}
